import { useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { WhaticketOfficialForm } from './WhaticketOfficialForm';
import { NotificamehubOfficialForm } from './NotificamehubOfficialForm';
import { useChannelConnection } from '@/hooks/useChannelConnections';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/lib/error-handler';
import { ChatProvider } from '@/types/channels';

interface EditChannelDialogProps {
  channelId: string | null;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Edit Channel Dialog - Mobile-first
 *
 * Carrega os dados do canal e permite editar configurações
 * Por segurança, as credenciais não são retornadas da API,
 * então o usuário deve preencher apenas se quiser atualizar
 */
export function EditChannelDialog({
  channelId,
  open,
  onClose,
  onSuccess,
}: EditChannelDialogProps) {
  const { toast } = useToast();
  const { data: channel, isLoading, error } = useChannelConnection(channelId || '');

  // Show error toast if channel fails to load
  useEffect(() => {
    if (error) {
      const message = getErrorMessage(error, 'Não foi possível carregar os dados do canal');
      toast({
        title: 'Erro ao carregar canal',
        description: message,
        variant: 'destructive',
      });
    }
  }, [error, toast]);

  // Don't render if no channelId
  if (!channelId) {
    return null;
  }

  const handleSuccess = () => {
    // Toast is already shown by WhaticketOfficialForm
    onClose();
    onSuccess?.();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Canal</DialogTitle>
          <DialogDescription>
            Atualize as configurações do canal. Por segurança, as credenciais não são exibidas.
            Preencha apenas os campos que deseja atualizar.
          </DialogDescription>
        </DialogHeader>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Error State */}
        {!!error && !isLoading && (
          <div className="py-6 text-center text-destructive">
            <p>Não foi possível carregar os dados do canal.</p>
            <p className="text-sm mt-2">Tente novamente mais tarde.</p>
          </div>
        )}

        {/* Form - render based on provider */}
        {channel && !isLoading && !error && (
          <>
            {channel.provider === ChatProvider.WHATICKET && (
              <WhaticketOfficialForm
                mode="edit"
                channelId={channelId}
                initialValues={{
                  name: channel.name,
                  apiUrl: channel.config?.apiUrl as string || '',
                  // apiKey is not returned for security - user must fill only if updating
                }}
                onSuccess={handleSuccess}
                onCancel={onClose}
              />
            )}
            {channel.provider === ChatProvider.NOTIFICAMEHUB && (
              <NotificamehubOfficialForm
                mode="edit"
                channelId={channelId}
                initialValues={{
                  name: channel.name,
                  channelId: channel.config?.channelId as string || '',
                  // apiToken is not returned for security - user must fill only if updating
                }}
                onSuccess={handleSuccess}
                onCancel={onClose}
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
